/**
 * Product page JavaScript
 * Handles variant selection and updates price, SKU, availability
 * Remembers last selected size across products
 */

class VariantSelector {
  constructor() {
    this.variantButtons = document.querySelectorAll('.variant-picker__button');
    this.variantData = this.getVariantData();
    this.storageKey = 'lastSelectedSize';
    this.init();
  }

  getVariantData() {
    const dataElement = document.querySelector('[data-variant-json]');
    return dataElement ? JSON.parse(dataElement.textContent) : [];
  }

  init() {
    if (!this.variantButtons.length || !this.variantData.length) return;

    // Try to pre-select the last selected size BEFORE showing content
    const preselected = this.preselectLastSize();

    // If we didn't preselect, ensure first variant is properly reflected in UI
    if (!preselected) {
      const selectedOptions = this.getSelectedOptions();
      const matchingVariant = this.findMatchingVariant(selectedOptions);
      if (matchingVariant) {
        this.updateVariant(matchingVariant);
      }
    }

    this.variantButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleVariantChange(e));
    });
  }

  preselectLastSize() {
    const lastSize = localStorage.getItem(this.storageKey);
    if (!lastSize) return false;

    // Find button with matching size label
    const matchingButton = Array.from(this.variantButtons).find(button => {
      return button.dataset.optionValue === lastSize;
    });

    if (!matchingButton) return false;

    // Check if this variant is available
    const selectedOptions = this.getSelectedOptionsWithOverride(lastSize);
    const matchingVariant = this.findMatchingVariant(selectedOptions);

    // Only preselect if variant exists and is available
    if (matchingVariant && matchingVariant.available) {
      // Remove active from all buttons in this fieldset
      const siblingButtons = matchingButton.closest('.variant-picker__buttons').querySelectorAll('.variant-picker__button');
      siblingButtons.forEach(btn => btn.classList.remove('active'));

      // Set this button as active
      matchingButton.classList.add('active');

      // Update the product info
      this.updateVariant(matchingVariant);
      return true;
    }

    return false;
  }

  getSelectedOptionsWithOverride(sizeOverride) {
    const options = {};
    document.querySelectorAll('.variant-picker__option').forEach((fieldset, index) => {
      const legend = fieldset.querySelector('legend');
      const isSize = legend && legend.textContent.trim().toLowerCase().includes('size');

      if (isSize && sizeOverride) {
        options[index + 1] = sizeOverride;
      } else {
        const activeButton = fieldset.querySelector('.variant-picker__button.active');
        if (activeButton) {
          options[index + 1] = activeButton.dataset.optionValue;
        }
      }
    });
    return options;
  }

  handleVariantChange(event) {
    const button = event.currentTarget;
    const optionValue = button.dataset.optionValue;
    const optionPosition = button.dataset.optionPosition;

    // Update active state
    const siblingButtons = button.closest('.variant-picker__buttons').querySelectorAll('.variant-picker__button');
    siblingButtons.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    // Save size selection to localStorage
    const fieldset = button.closest('.variant-picker__option');
    const legend = fieldset.querySelector('legend');
    const isSize = legend && legend.textContent.trim().toLowerCase().includes('size');

    if (isSize) {
      localStorage.setItem(this.storageKey, optionValue);
    }

    // Find matching variant
    const selectedOptions = this.getSelectedOptions();
    const matchingVariant = this.findMatchingVariant(selectedOptions);

    if (matchingVariant) {
      this.updateVariant(matchingVariant);
    }
  }

  getSelectedOptions() {
    const options = {};
    document.querySelectorAll('.variant-picker__option').forEach((fieldset, index) => {
      const activeButton = fieldset.querySelector('.variant-picker__button.active');
      if (activeButton) {
        options[index + 1] = activeButton.dataset.optionValue;
      }
    });
    return options;
  }

  findMatchingVariant(selectedOptions) {
    return this.variantData.find(variant => {
      return Object.keys(selectedOptions).every(position => {
        const optionKey = `option${position}`;
        return variant[optionKey] === selectedOptions[position];
      });
    });
  }

  updateVariant(variant) {
    // Update hidden variant input
    const variantInput = document.querySelector('[data-variant-input]');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update price and original price
    window.renderProductPrice(variant);

    // Update SKU
    const skuElement = document.querySelector('[data-sku]');
    if (skuElement && variant.sku) {
      skuElement.textContent = variant.sku;
    }

    // Update add to cart button
    const addToCartButton = document.querySelector('[data-add-to-cart]');
    if (addToCartButton) {
      if (variant.available) {
        addToCartButton.disabled = false;
        addToCartButton.querySelector('span').textContent = window.variantStrings?.addToBasket || 'Add to basket';
      } else {
        addToCartButton.disabled = true;
        addToCartButton.querySelector('span').textContent = window.variantStrings?.soldOut || 'Sold out';
      }
    }
  }

  formatMoney(cents) {
    return formatMoney(cents);
  }
}

// Matches the server-rendered `| money` output, and the formatter in script.js.
function formatMoney(cents) {
  return '€' + (cents / 100).toFixed(2).replace('.', ',');
}

/**
 * Single source of truth for the product price after a variant change.
 *
 * Both variant pickers on this page call it — the one in this file and the one
 * in script.js — so the two can no longer write conflicting values into
 * [data-price].
 *
 * The sale percentage comes from the product's sale.percentage metafield, which
 * snippets/price.liquid puts on the wrapper. Rounding matches that snippet, so
 * the price never shifts when switching variants.
 */
window.renderProductPrice = function (variant) {
  if (!variant) return;

  // Product cards carry the same attributes, so stay inside the main product
  // section — related products render below it on this page.
  const scope = document.querySelector('section.product') || document;

  const priceElement = scope.querySelector('[data-price]');
  const display = priceElement?.closest('.price-display');
  const percentage = Number(display?.dataset.salePercentage) || 0;
  const onSale = percentage > 0 && percentage < 100;

  let payable = variant.price;
  let original = 0;

  if (onSale) {
    payable = variant.price - Math.round((variant.price * percentage) / 100);
    original = variant.price;
  } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
    original = variant.compare_at_price;
  }

  if (priceElement) {
    priceElement.textContent = formatMoney(payable);
    display?.classList.toggle('price-display--on-sale', original > 0);
  }

  const originalElement = scope.querySelector('[data-compare-price]');
  if (originalElement) {
    originalElement.textContent = original > 0 ? formatMoney(original) : '';
    originalElement.hidden = original === 0;
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

function init() {
  new VariantSelector();
}
